import { Injectable, signal } from '@angular/core';

/** Directives a page CSP would block a frame's `blob:` document under. */
const FRAME_DIRECTIVES = /^(frame|child|default)-src/;

/**
 * Loads a built mail document into a reader frame.
 *
 * A `blob:` document, not `srcdoc`: a srcdoc document's referrer source is its parent page's URL, and
 * Chrome sent that on CSS-initiated image fetches whatever the referrer-policy metas said. A blob: URL
 * is a local scheme, so nothing the document fetches has a referrer source at all. Should a page CSP
 * block blob: frames, the page reports it and every reader falls back to srcdoc.
 */
@Injectable({ providedIn: 'root' })
export class EmailFrameLoader {
  /** Bumped when blob: documents turn out to be blocked, so readers render again with srcdoc. */
  public readonly fallbacks = signal(0);

  private blobBlocked = false;

  constructor() {
    document.addEventListener('securitypolicyviolation', (event) => {
      if (!this.blobBlocked && event.blockedURI.startsWith('blob') && FRAME_DIRECTIVES.test(event.effectiveDirective)) {
        this.blobBlocked = true;
        this.fallbacks.update((count) => count + 1);
      }
    });
  }

  /**
   * Points the frame at `html`. Returns the blob: URL the caller revokes with the render, or `null`
   * for the srcdoc fallback. `location.replace`, not `src`: a frame navigated by `src` to a new URL
   * pushes a history entry per message, and Back would walk the frame through earlier mails.
   */
  public load(frame: HTMLIFrameElement, html: string): string | null {
    if (this.blobBlocked) {
      // Never bound in a template: Angular would sanitize srcdoc as HTML and strip the <style> blocks.
      frame.srcdoc = html;
      return null;
    }
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
    frame.contentWindow?.location.replace(url);
    return url;
  }
}
